import { useEffect, useState } from 'react';
import { useVaultStore, RotationJob } from '../../../store/vaultStore';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Input } from '../../../components/Input';
import { Textarea } from '../../../components/Textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/Select';
import { RefreshCw, Plus } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_VARIANT: Record<string, any> = {
  scheduled: 'secondary', in_progress: 'warning', succeeded: 'success', failed: 'destructive', cancelled: 'muted',
};

export function RotationCenter() {
  const store = useVaultStore();
  const [jobs, setJobs] = useState<RotationJob[]>([]);
  const [loading, setLoading] = useState(true);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ itemId: '', method: 'manual' });

  const [outcome, setOutcome] = useState<RotationJob | null>(null);
  const [outcomeForm, setOutcomeForm] = useState({ status: 'succeeded', externalChanged: false, note: '' });

  const load = async () => {
    setLoading(true);
    try { setJobs(await store.listRotationJobs()); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Failed to load rotation jobs'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const schedule = async () => {
    if (!scheduleForm.itemId.trim()) { toast.error('Credential (item) ID is required'); return; }
    try {
      await store.scheduleRotation(scheduleForm.itemId.trim(), { method: scheduleForm.method });
      toast.success('Rotation started');
      setScheduleOpen(false);
      setScheduleForm({ itemId: '', method: 'manual' });
      load();
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Failed to schedule rotation'); }
  };

  const openOutcome = (j: RotationJob) => {
    setOutcome(j);
    setOutcomeForm({ status: 'succeeded', externalChanged: false, note: '' });
  };

  const saveOutcome = async () => {
    if (!outcome) return;
    if (outcomeForm.status === 'succeeded' && !outcomeForm.externalChanged) {
      toast.error('Confirm the external credential was changed before marking succeeded');
      return;
    }
    try {
      await store.completeRotation(outcome.id, outcomeForm);
      toast.success('Rotation outcome recorded');
      setOutcome(null);
      load();
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Failed to record outcome'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Rotation</h1>
          <p className="text-muted-foreground">Track credential rotations. A reminder is not a rotation — success means the external secret was actually changed.</p>
        </div>
        <Button variant="primary" onClick={() => setScheduleOpen(true)}><Plus className="w-4 h-4 mr-2" /> Start rotation</Button>
      </div>

      <Card>
        <CardHeader><h3 className="text-foreground font-display text-section-title">Rotation jobs</h3></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading…</div>
          ) : jobs.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground">No rotation jobs yet.</div>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                <tr>
                  <th className="px-6 py-3">Credential</th>
                  <th className="px-6 py-3">Method</th>
                  <th className="px-6 py-3">External changed</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Created</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {jobs.map((j) => (
                  <tr key={j.id} className="hover:bg-secondary/20">
                    <td className="px-6 py-4 font-mono text-xs">{j.itemId.slice(0, 8)}…</td>
                    <td className="px-6 py-4 capitalize">{j.method}</td>
                    <td className="px-6 py-4">{j.externalChanged ? 'Yes' : 'No'}</td>
                    <td className="px-6 py-4"><Badge variant={STATUS_VARIANT[j.status]}>{j.status.replace('_', ' ').toUpperCase()}</Badge></td>
                    <td className="px-6 py-4 text-muted-foreground">{new Date(j.createdAt).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-right">
                      {(j.status === 'scheduled' || j.status === 'in_progress') && (
                        <Button variant="ghost" size="xs" className="gap-1" onClick={() => openOutcome(j)}><RefreshCw className="w-3.5 h-3.5" /> Record outcome</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Start rotation */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start a rotation</DialogTitle>
            <DialogDescription>Begin tracking a rotation for a credential you can edit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Credential (item) ID</label>
              <Input value={scheduleForm.itemId} onChange={(e) => setScheduleForm((f) => ({ ...f, itemId: e.target.value }))} className="font-mono" placeholder="credential UUID" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Method</label>
              <Select value={scheduleForm.method} onValueChange={(v) => setScheduleForm((f) => ({ ...f, method: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="automatic">Automatic</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setScheduleOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={schedule}>Start</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record outcome */}
      <Dialog open={!!outcome} onOpenChange={(o) => !o && setOutcome(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record rotation outcome</DialogTitle>
            <DialogDescription>Marking succeeded requires confirming the external credential was actually changed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Outcome</label>
              <Select value={outcomeForm.status} onValueChange={(v) => setOutcomeForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="succeeded">Succeeded</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {outcomeForm.status === 'succeeded' && (
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" className="size-4 accent-primary" checked={outcomeForm.externalChanged} onChange={(e) => setOutcomeForm((f) => ({ ...f, externalChanged: e.target.checked }))} />
                I confirm the external credential was changed and verified
              </label>
            )}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Note</label>
              <Textarea rows={2} value={outcomeForm.note} onChange={(e) => setOutcomeForm((f) => ({ ...f, note: e.target.value }))} placeholder="Optional note (no secrets)" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOutcome(null)}>Cancel</Button>
            <Button variant="primary" onClick={saveOutcome}>Save outcome</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
